/**
 * Client for the public activity endpoints (apps/backend/src/activity/) —
 * the live ticker / online-count from the galaxy plan. Deliberately no
 * auth: these routes carry no bearer token and are meant to be visible on
 * EntryView before anyone has signed in.
 *
 * Everything here fails silently (empty array / null) rather than
 * throwing — this is decorative social-proof, never load-bearing. A
 * network hiccup or the backend briefly having zero bots must not show an
 * error anywhere the player would notice.
 */

import { getApiUrl } from './backend-client';

export interface ActivityEvent {
  at: string;
  message: string;
  kind: string;
}

export class ActivityClient {
  constructor(private readonly baseUrl: string = getApiUrl()) {}

  async feed(limit = 10): Promise<ActivityEvent[]> {
    try {
      const res = await fetch(`${this.baseUrl}/activity/feed?limit=${limit}`);
      if (!res.ok) return [];
      const body = (await res.json()) as { events?: ActivityEvent[] };
      return body.events ?? [];
    } catch {
      return [];
    }
  }

  async onlineCount(): Promise<number | null> {
    try {
      const res = await fetch(`${this.baseUrl}/activity/online-count`);
      if (!res.ok) return null;
      const body = (await res.json()) as { count?: number };
      return typeof body.count === 'number' ? body.count : null;
    } catch {
      return null;
    }
  }
}

/** One shared instance — stateless and public, no need to thread it through props. */
export const activityClient = new ActivityClient();
